import DownloadIcon from '@mui/icons-material/Download';
import { Box, Button, Card, CardContent, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useState } from 'react';
import type { CallMetricsByTeam } from '../../../interfaces/calls';
import { downloadFile, metricsToCSV } from '../../../utils/exportUtils';

interface TeamsTabProps {
  teams: CallMetricsByTeam[];
  comparison?: CallMetricsByTeam[];
  renderMetricCard: (
    label: string,
    value: string | number,
    prevValue?: string | number,
    numericValue?: number,
    inverted?: boolean,
    formatter?: (val: number) => string,
  ) => ReactNode;
  formatDuration: (seconds: number) => string;
  currentStartDate?: string;
  currentEndDate?: string;
  previousStartDate?: string;
  previousEndDate?: string;
}

export const TeamsTab = ({
  teams,
  comparison,
  renderMetricCard,
  formatDuration,
  currentStartDate,
  currentEndDate,
  previousStartDate,
  previousEndDate,
}: TeamsTabProps) => {
  const [expandedTeams, setExpandedTeams] = useState<Record<number, boolean>>({});

  const toggleTeam = (teamId: number) => {
    setExpandedTeams((prev) => ({
      ...prev,
      [teamId]: !prev[teamId],
    }));
  };

  const handleExportTeam = (team: CallMetricsByTeam, comparisonTeam?: CallMetricsByTeam) => {
    if (!currentStartDate || !currentEndDate) return;

    const teamMetrics = [
      { label: 'Team Name', value: team.teamName, previousValue: comparisonTeam?.teamName },
      { label: 'Total Calls', value: team.totalCalls, previousValue: comparisonTeam?.totalCalls },
      { label: 'Inbound', value: team.inboundCalls, previousValue: comparisonTeam?.inboundCalls },
      { label: 'Outbound', value: team.outboundCalls, previousValue: comparisonTeam?.outboundCalls },
      { label: 'Answered', value: team.answeredCalls, previousValue: comparisonTeam?.answeredCalls },
      { label: 'Average Duration (seconds)', value: team.averageDuration, previousValue: comparisonTeam?.averageDuration },
      { label: 'Average Wait Time (seconds)', value: team.averageWaitTime, previousValue: comparisonTeam?.averageWaitTime },
      { label: 'Average Answer Time (seconds)', value: team.averageAnswerTime, previousValue: comparisonTeam?.averageAnswerTime },
    ];

    // Add inbound details if expanded
    if (team.inboundDetails) {
      teamMetrics.push(
        { label: 'Inbound - Answered', value: team.inboundDetails.answered, previousValue: comparisonTeam?.inboundDetails?.answered },
        { label: 'Inbound - Missed', value: team.inboundDetails.missed, previousValue: comparisonTeam?.inboundDetails?.missed },
        { label: 'Inbound - Abandoned', value: team.inboundDetails.abandoned, previousValue: comparisonTeam?.inboundDetails?.abandoned },
        { label: 'Inbound - Answer Rate (%)', value: team.inboundDetails.answerRate?.toFixed(1) ?? '0.0', previousValue: comparisonTeam?.inboundDetails?.answerRate?.toFixed(1) },
      );
    }

    // Add outbound details if expanded
    if (team.outboundDetails) {
      teamMetrics.push(
        { label: 'Outbound - Completed', value: team.outboundDetails.completed, previousValue: comparisonTeam?.outboundDetails?.completed },
        { label: 'Outbound - No Answer', value: team.outboundDetails.noAnswer, previousValue: comparisonTeam?.outboundDetails?.noAnswer },
        { label: 'Outbound - Failed', value: team.outboundDetails.failed, previousValue: comparisonTeam?.outboundDetails?.failed },
        { label: 'Outbound - Success Rate (%)', value: team.outboundDetails.successRate?.toFixed(1) ?? '0.0', previousValue: comparisonTeam?.outboundDetails?.successRate?.toFixed(1) },
      );
    }

    const csv = metricsToCSV(teamMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate);
    downloadFile(csv, `team-${team.teamName.replace(/\s+/g, '-').toLowerCase()}-metrics.csv`);
  };

  return (
    <Box>
      <Typography variant='h6' gutterBottom sx={{ mt: 2, mb: 2 }}>
        Team Metrics
      </Typography>

      <Grid container spacing={3}>
        {teams.map((team) => {
          const comparisonTeam = comparison?.find((t) => t.teamId === team.teamId);
          const isExpanded = expandedTeams[team.teamId] || false;

          return (
            <Grid item xs={12} key={team.teamId}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant='h6'>🏢 {team.teamName}</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title='Export team metrics'>
                        <IconButton size='small' onClick={() => handleExportTeam(team, comparisonTeam)} sx={{ color: '#1a667a' }}>
                          <DownloadIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      <Button size='small' variant='outlined' onClick={() => toggleTeam(team.teamId)}>
                        {isExpanded ? 'Collapse Details' : 'Expand Details'}
                      </Button>
                    </Box>
                  </Box>

                  {/* Basic Team Metrics */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      {renderMetricCard('Total Calls', team.totalCalls, comparisonTeam?.totalCalls, undefined, false)}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      {renderMetricCard('Inbound', team.inboundCalls, comparisonTeam?.inboundCalls, undefined, false)}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      {renderMetricCard('Outbound', team.outboundCalls, comparisonTeam?.outboundCalls, undefined, false)}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      {renderMetricCard('Answered', team.answeredCalls, comparisonTeam?.answeredCalls, undefined, false)}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      {renderMetricCard(
                        'Avg Duration',
                        formatDuration(team.averageDuration),
                        comparisonTeam?.averageDuration,
                        team.averageDuration,
                        false,
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      {renderMetricCard(
                        'Avg Wait Time',
                        formatDuration(team.averageWaitTime),
                        comparisonTeam?.averageWaitTime,
                        team.averageWaitTime,
                        true,
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      {renderMetricCard(
                        'Avg Answer Time',
                        formatDuration(team.averageAnswerTime),
                        comparisonTeam?.averageAnswerTime,
                        team.averageAnswerTime,
                        true,
                      )}
                    </Grid>

                    {/* Expanded Details */}
                    {isExpanded && team.inboundDetails && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant='subtitle2' gutterBottom sx={{ mt: 2 }}>
                            Inbound Breakdown
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          {renderMetricCard(
                            'Inbound Answered',
                            team.inboundDetails.answered,
                            comparisonTeam?.inboundDetails?.answered,
                          )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          {renderMetricCard(
                            'Inbound Abandoned',
                            team.inboundDetails.abandoned,
                            comparisonTeam?.inboundDetails?.abandoned,
                            undefined,
                            true,
                          )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          {renderMetricCard(
                            'Inbound Missed',
                            team.inboundDetails.missed,
                            comparisonTeam?.inboundDetails?.missed,
                            undefined,
                            true,
                          )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          {renderMetricCard(
                            'Answer Rate',
                            `${team.inboundDetails.answerRate.toFixed(1)}%`,
                            comparisonTeam?.inboundDetails?.answerRate,
                            team.inboundDetails.answerRate,
                            false,
                            (val: number) => `${val.toFixed(1)}%`,
                          )}
                        </Grid>
                      </>
                    )}

                    {isExpanded && team.outboundDetails && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant='subtitle2' gutterBottom sx={{ mt: 2 }}>
                            Outbound Breakdown
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          {renderMetricCard(
                            'Outbound Completed',
                            team.outboundDetails.completed,
                            comparisonTeam?.outboundDetails?.completed,
                          )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          {renderMetricCard(
                            'Outbound Failed',
                            team.outboundDetails.failed,
                            comparisonTeam?.outboundDetails?.failed,
                            undefined,
                            true,
                          )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          {renderMetricCard(
                            'Success Rate',
                            `${team.outboundDetails.successRate.toFixed(1)}%`,
                            comparisonTeam?.outboundDetails?.successRate,
                            team.outboundDetails.successRate,
                            false,
                            (val: number) => `${val.toFixed(1)}%`,
                          )}
                        </Grid>
                      </>
                    )}

                    {isExpanded && team.ratingMetrics && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant='subtitle2' gutterBottom sx={{ mt: 2 }}>
                            Rating Quality
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          {renderMetricCard(
                            'Evaluated Calls',
                            team.ratingMetrics.evaluatedCalls,
                            comparisonTeam?.ratingMetrics?.evaluatedCalls,
                          )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          {renderMetricCard(
                            'Avg Rating',
                            team.ratingMetrics.averageRating.toFixed(2),
                            comparisonTeam?.ratingMetrics?.averageRating,
                            team.ratingMetrics.averageRating,
                            false,
                            (val: number) => val.toFixed(2),
                          )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          {renderMetricCard(
                            'Positive Rate',
                            `${team.ratingMetrics.positiveRatingRate.toFixed(1)}%`,
                            comparisonTeam?.ratingMetrics?.positiveRatingRate,
                            team.ratingMetrics.positiveRatingRate,
                            false,
                            (val: number) => `${val.toFixed(1)}%`,
                          )}
                        </Grid>
                      </>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
